import Card from '../components/card/Card';
import Tooltip from '../components/tooltip/Tooltip';
import Span from '../components/texts/span/Span';
import Badge from '../components/badge/Badge';
import P from '../components/texts/p/P';
import Icon from '../components/icon/Icon';
import TruncatedTextWithTooltip from '../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import { formatCuit, formatCurrency } from '../utils/Masks';
import { format } from 'date-fns';

import { Nullable } from '../types/interfaces';
import {
  getBadgeStatus,
  getStatusText,
  getTooltipText,
} from './applicationStatusUtils';
import { getBalanceReadStatus, getBalanceProcessingErrorMessage } from './balanceReadStatusUtils';

export interface IRow {
  cuit: string;
  id: number;
  commercial: string;
  companyName: string;
  producerName: string;
  producerLastName: string;
  updateDate: string;
  status: number;
  totalArs: number;
  totalUsd: number;
  approved_total_ars: Nullable<number>;
  approved_total_usd: Nullable<number>;
  usuario?: string;
  referido?: string;
  balanceProcessingStatus?: string | null;
  balanceProcessingErrorCode?: string | null;
  balanceProcessingErrorContext?: string | null;
}

export function getDashboardColumns({
  handleRowClicked,
  handleViewApplication,
  showApprovedAmounts = false,
  showBalanceReadStatus = false,
}: {
  handleRowClicked: (row: IRow) => void;
  handleViewApplication?: (row: IRow) => void;
  showApprovedAmounts?: boolean;
  showBalanceReadStatus?: boolean;
}) {
  return [
    {
      name: 'CUIT',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={formatCuit(row?.cuit || '')}>
            <P type='4' variant='neutral'>
              {formatCuit(row?.cuit || '')}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '55px',
      grow: 0.5,
    },
    {
      name: 'Usuario',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.usuario || 'N/A'}>
            <P type='4' variant='neutral'>
              {row?.usuario || 'N/A'}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '55px',
      grow: 0.6,
    },
    {
      name: 'Referido',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.referido || 'N/A'}>
            <P type='4' variant='neutral'>
              {row?.referido || 'N/A'}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '55px',
      grow: 0.6,
    },
    {
      name: 'Comercial',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.commercial || 'Sin asignar'}>
            <P type='4' variant='neutral'>
              {row?.commercial || 'Sin asignar'}
            </P>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '45px',
      grow: 0.6,
    },
    {
      name: 'Razón social',
      selector: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          <TruncatedTextWithTooltip text={row?.companyName || ''}>
            <Span type='4' variant='neutral'>
              {row?.companyName || ''}
            </Span>
          </TruncatedTextWithTooltip>
        </Card>
      ),
      minWidth: '50px',
      grow: 0.7,
    },
    {
      name: 'Modificado',
      cell: (row: IRow) => {
        const date = row?.updateDate ? new Date(row?.updateDate) : null;
        return (
          <Card onClick={() => handleRowClicked(row)}>
            <P type='4' variant='neutral'>
              {date ? format(date, 'dd/MM/yyyy') : ''}
            </P>
          </Card>
        );
      },
      minWidth: '50px',
      grow: 0.4,
    },
    {
      name: showApprovedAmounts ? 'Monto aprobado' : 'Monto',
      cell: (row: IRow) => (
        <Card onClick={() => handleRowClicked(row)}>
          {showApprovedAmounts ? (
            <>
              <Card display='flex' gap='2px' margin={{ bottom: '1' }}>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  ARS
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.approved_total_ars ?? 0, true)}
                </P>
              </Card>
              <Card display='flex' gap='2px'>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  USD
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.approved_total_usd ?? 0, true)}
                </P>
              </Card>
            </>
          ) : (
            <>
              <Card display='flex' gap='2px' margin={{ bottom: '1' }}>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  ARS
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.totalArs ?? 0, true)}
                </P>
              </Card>
              <Card display='flex' gap='2px'>
                <P type='2' withMargin={false} variant='neutral' weight='bold'>
                  USD
                </P>
                <P type='2' withMargin={false} variant='neutral'>
                  {formatCurrency(row.totalUsd ?? 0, true)}
                </P>
              </Card>
            </>
          )}
        </Card>
      ),
      minWidth: '45px',
      grow: 0.6,
    },
    ...(showBalanceReadStatus
      ? [
          {
            name: 'Estado lectura',
            cell: (row: IRow) => {
              const readStatus = getBalanceReadStatus(row.balanceProcessingStatus);
              const showFailureTooltip =
                row.balanceProcessingStatus === 'FAILED' &&
                row.balanceProcessingErrorCode;
              const failureMessage = getBalanceProcessingErrorMessage(
                row.balanceProcessingErrorCode,
                row.balanceProcessingErrorContext
              );
              const cellContent = (
                <Card onClick={() => handleRowClicked(row)}>
                  <Card display='flex' alignItems='center' gap='8px'>
                    {readStatus.icon && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px', flexShrink: 0, overflow: 'hidden' }}>
                        <span style={{ display: 'inline-block', width: '16px', height: '16px', lineHeight: 0 }}>
                          <Icon
                            name={readStatus.icon}
                            variant={readStatus.variant}
                            width={16}
                            height={16}
                          />
                        </span>
                      </span>
                    )}
                    <P type='4' variant='neutral'>
                      {readStatus.text}
                    </P>
                  </Card>
                </Card>
              );
              return showFailureTooltip ? (
                <Tooltip msg={failureMessage}>
                  {cellContent}
                </Tooltip>
              ) : (
                cellContent
              );
            },
            minWidth: '45px',
            grow: 0.5,
          },
          {
            name: 'Solicitud',
            cell: (row: IRow) => (
              <div
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => {
                  if (handleViewApplication) {
                    handleViewApplication(row);
                  }
                }}
                role='button'
                aria-label='Ver solicitud'
              >
                <Icon name='view_outline' variant='info' modifier='strong' />
              </div>
            ),
            center: true,
            minWidth: '45px',
            grow: 0.5,
          },
        ]
      : []),
    {
      name: 'Estado',
      cell: (row: IRow) => {
        const tooltipText = getTooltipText(row?.status);
        const badgeVariant = getBadgeStatus(row?.status);
        const statusText = getStatusText(row?.status);

        return (
          <Card alignItems='center' onClick={() => handleRowClicked(row)}>
            {tooltipText ? (
              <Tooltip
                position='top'
                msg={
                  <Span type='2' variant='neutral' modifier='inverted'>
                    {tooltipText}
                  </Span>
                }
              >
                <Badge variant={badgeVariant} type='text'>
                  {statusText}
                </Badge>
              </Tooltip>
            ) : (
              <Badge variant={badgeVariant} type='text'>
                {statusText}
              </Badge>
            )}
          </Card>
        );
      },
      minWidth: '55px',
      grow: 0.7,
    },
  ];
}
